<template>
    <div class="login-overlay" @click.self="closeModal" @keydown.esc="closeModal">
        <div class="login-modal">
            <button class="login-close-button" @click="closeModal" aria-label="Close">
                <vue-feather type="x" size="20" />
            </button>
            <div class="login-container">
                <div class="login-left">
                    <div class="auth-wrapper">
                        <authenticator :key="currentLanguage" :sign-up-attributes="['email', 'name']">
                            <template v-slot>
                                <div class="login-success">
                                    <p>{{ authText('signedInRedirecting') }}</p>
                                </div>
                            </template>
                        </authenticator>
                    </div>
                </div>
                <div class="login-right">
                    <div class="login-right-content">
                        <template v-if="!isSignUp">
                            <h2>{{ authText('newHereTitle') }}</h2>
                            <p>{{ authText('newHereBody') }}</p>
                            <button class="cta-button" @click="switchToSignUp">{{ authText('signUpCta') }}</button>
                        </template>
                        <template v-else>
                            <h2>{{ authText('welcomeBackTitle') }}</h2>
                            <p>{{ authText('alreadyHaveAccount') }}</p>
                            <button class="cta-button" @click="switchToSignIn">{{ authText('loginCta') }}</button>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { Hub, I18n } from 'aws-amplify';
import { track as analyticsTrack } from '../utils/analytics';

const AUTH_VOCABULARY = {
    ta: {
        'Sign In': 'உள்நுழைக',
        'Sign in': 'உள்நுழைக',
        'Signing In': 'உள்நுழைகிறது',
        'Signing in': 'உள்நுழைகிறது',
        'Signing In...': 'உள்நுழைகிறது...',
        'Signing in...': 'உள்நுழைகிறது...',
        'Sign Up': 'பதிவு செய்க',
        'Sign up': 'பதிவு செய்க',
        'Signing Up': 'பதிவு செய்கிறது',
        'Signing up': 'பதிவு செய்கிறது',
        'Signing Up...': 'பதிவு செய்கிறது...',
        'Signing up...': 'பதிவு செய்கிறது...',
        'Create Account': 'கணக்கை உருவாக்குக',
        'Create account': 'கணக்கை உருவாக்குக',
        'Creating Account': 'கணக்கு உருவாக்கப்படுகிறது',
        'Creating account': 'கணக்கு உருவாக்கப்படுகிறது',
        'Creating Account...': 'கணக்கு உருவாக்கப்படுகிறது...',
        'Creating account...': 'கணக்கு உருவாக்கப்படுகிறது...',
        'Username': 'பயனர்பெயர்',
        'Enter your Username': 'பயனர்பெயரை உள்ளிடவும்',
        'Enter your username': 'பயனர்பெயரை உள்ளிடவும்',
        'Password': 'கடவுச்சொல்',
        'Enter your Password': 'கடவுச்சொல்லை உள்ளிடவும்',
        'Enter your password': 'கடவுச்சொல்லை உள்ளிடவும்',
        'Email': 'மின்னஞ்சல்',
        'Enter your Email': 'மின்னஞ்சலை உள்ளிடவும்',
        'Enter your email': 'மின்னஞ்சலை உள்ளிடவும்',
        'Name': 'பெயர்',
        'Enter your Name': 'பெயரை உள்ளிடவும்',
        'Enter your name': 'பெயரை உள்ளிடவும்',
        'Forgot your password?': 'கடவுச்சொல்லை மறந்துவிட்டீர்களா?',
        'Reset Password': 'கடவுச்சொல்லை மீட்டமை',
        'Reset password': 'கடவுச்சொல்லை மீட்டமை',
        'Send Code': 'குறியீட்டை அனுப்புக',
        'Send code': 'குறியீட்டை அனுப்புக',
        'Sending Code': 'குறியீடு அனுப்பப்படுகிறது',
        'Sending code': 'குறியீடு அனுப்பப்படுகிறது',
        'Sending Code...': 'குறியீடு அனுப்பப்படுகிறது...',
        'Sending code...': 'குறியீடு அனுப்பப்படுகிறது...',
        'Code': 'குறியீடு',
        'Confirmation Code': 'உறுதிப்படுத்தும் குறியீடு',
        'Enter your code': 'குறியீட்டை உள்ளிடவும்',
        'New Password': 'புதிய கடவுச்சொல்',
        'Enter your new password': 'புதிய கடவுச்சொல்லை உள்ளிடவும்',
        'Change Password': 'கடவுச்சொல்லை மாற்று',
        'Back to Sign In': 'உள்நுழைவுக்கு திரும்புக',
        'Have an account?': 'ஏற்கனவே கணக்கு உள்ளதா?',
        'No account?': 'கணக்கு இல்லையா?',
        'Confirm': 'உறுதிப்படுத்து',
        'Submit': 'சமர்ப்பி',
        'Submitting': 'சமர்ப்பிக்கிறது',
        'Submitting...': 'சமர்ப்பிக்கிறது...',
        'Loading': 'ஏற்றுகிறது',
        'Loading...': 'ஏற்றுகிறது...',
        'Show password': 'கடவுச்சொல்லைக் காட்டு',
        'Hide password': 'கடவுச்சொல்லை மறை',
        'Username cannot be empty': 'பயனர்பெயர் காலியாக இருக்க முடியாது',
        'Password cannot be empty': 'கடவுச்சொல் காலியாக இருக்க முடியாது',
        'User does not exist.': 'இந்த பயனர் இல்லை.',
        'Incorrect username or password.': 'பயனர்பெயர் அல்லது கடவுச்சொல் தவறானது.',
        'Invalid verification code provided, please try again.': 'தவறான உறுதிப்படுத்தும் குறியீடு. மீண்டும் முயற்சிக்கவும்.',
        'An account with the given email already exists.': 'இந்த மின்னஞ்சலுடன் ஏற்கனவே ஒரு கணக்கு உள்ளது.'
    }
};

const LOGIN_TEXT = {
    EN: {
        signedInRedirecting: 'You are signed in. Redirecting...',
        newHereTitle: 'New Here?',
        newHereBody: "Start browsing new features. Let's get started!",
        signUpCta: 'Sign Up!',
        welcomeBackTitle: 'Welcome Back!',
        alreadyHaveAccount: 'Already have an account?',
        loginCta: 'Log In',
        usernameCaseHint: 'Username is case-sensitive.',
        forgotInstructionWithUsername: 'We sent a password reset code to the email associated with username: {username}. Enter the code below to reset your password. If you are having trouble accessing this account, you can create a new account instead.',
        forgotInstruction: 'We sent a password reset code to the email associated with your username. Enter the code below to reset your password. If you are having trouble accessing this account, you can create a new account instead.'
    },
    TA: {
        signedInRedirecting: 'நீங்கள் உள்நுழைந்துள்ளீர்கள். திருப்பி அனுப்பப்படுகிறது...',
        newHereTitle: 'புதியவரா?',
        newHereBody: 'புதிய அம்சங்களைப் பார்க்க தொடங்குங்கள்.',
        signUpCta: 'பதிவு செய்க',
        welcomeBackTitle: 'மீண்டும் வரவேற்கிறோம்!',
        alreadyHaveAccount: 'ஏற்கனவே கணக்கு உள்ளதா?',
        loginCta: 'உள்நுழைக',
        usernameCaseHint: 'பயனர்பெயரில் பெரிய/சிறிய எழுத்து வேறுபாடு கவனிக்கப்படும்.',
        forgotInstructionWithUsername: '{username} என்ற பயனர்பெயருடன் இணைக்கப்பட்ட மின்னஞ்சலுக்கு கடவுச்சொல் மீட்டமைப்பு குறியீட்டை அனுப்பியுள்ளோம். கீழே குறியீட்டை உள்ளிட்டு கடவுச்சொல்லை மாற்றுங்கள். இந்த கணக்கை அணுக சிரமம் இருந்தால், புதிய கணக்கை உருவாக்கலாம்.',
        forgotInstruction: 'உங்கள் பயனர்பெயருடன் இணைக்கப்பட்ட மின்னஞ்சலுக்கு கடவுச்சொல் மீட்டமைப்பு குறியீட்டை அனுப்பியுள்ளோம். கீழே குறியீட்டை உள்ளிட்டு கடவுச்சொல்லை மாற்றுங்கள். இந்த கணக்கை அணுக சிரமம் இருந்தால், புதிய கணக்கை உருவாக்கலாம்.'
    }
};

let authVocabularyRegistered = false;

function registerAuthVocabulary() {
    if (authVocabularyRegistered) return;
    I18n.putVocabularies(AUTH_VOCABULARY);
    authVocabularyRegistered = true;
}

export default {
    name: 'LoginModal',
    inject: {
        languageRef: {
            from: 'currentLanguage',
            default: 'EN'
        }
    },
    emits: ['close'],
    data() {
        return {
            isSignUp: false,
            observer: null,
            formListenerAttached: false,
            lastAuthFlow: '',
            lastAuthStep: '',
            lastErrorCode: '',
            lastErrorTrackedAt: 0,
            lastForgotPasswordUsername: '',
            authEnhancementPending: false,
            closeTracked: false
        };
    },
    computed: {
        currentLanguage() {
            return this.languageRef?.value || this.languageRef || 'EN';
        },
        isTamil() {
            return this.currentLanguage === 'TA';
        }
    },
    watch: {
        currentLanguage() {
            this.applyAuthLanguage();
            this.$nextTick(() => this.scheduleAuthEnhancements());
        }
    },
    created() {
        this.applyAuthLanguage();
    },
    mounted() {
        analyticsTrack('auth_modal_opened', {
            flow: this.inferAuthFlow(),
            path: this.$route?.fullPath || window.location.pathname
        });

        this.hubListener = Hub.listen('auth', (data) => {
            const { payload } = data;
            if (payload.event === 'signIn') {
                this.$emit('close');
            } else if (payload.event && payload.event.endsWith('_failure')) {
                this.trackAuthFailure(payload.data, 'hub');
            }
        });

        this.$nextTick(() => {
            this.setupTabObserver();
        });

        // Focus the overlay for ESC key support
        this.$el.focus();
    },
    beforeUnmount() {
        this.trackModalClosed();
        if (this.hubListener) {
            this.hubListener();
        }
        if (this.observer) {
            this.observer.disconnect();
        }
    },
    methods: {
        applyAuthLanguage() {
            registerAuthVocabulary();
            I18n.setLanguage(this.isTamil ? 'ta' : 'en');
        },
        authText(key, replacements = {}) {
            const messages = LOGIN_TEXT[this.currentLanguage] || LOGIN_TEXT.EN;
            let text = messages[key] || LOGIN_TEXT.EN[key] || key;
            Object.entries(replacements).forEach(([name, value]) => {
                text = text.replace(`{${name}}`, value);
            });
            return text;
        },
        localizeAuthRuntimeText() {
            if (!this.isTamil) return;

            const runtimeTranslations = {
                'signing in': 'உள்நுழைகிறது',
                'signing in...': 'உள்நுழைகிறது...',
                'signing up': 'பதிவு செய்கிறது',
                'signing up...': 'பதிவு செய்கிறது...',
                'creating account': 'கணக்கு உருவாக்கப்படுகிறது',
                'creating account...': 'கணக்கு உருவாக்கப்படுகிறது...',
                'sending code': 'குறியீடு அனுப்பப்படுகிறது',
                'sending code...': 'குறியீடு அனுப்பப்படுகிறது...',
                'submitting': 'சமர்ப்பிக்கிறது',
                'submitting...': 'சமர்ப்பிக்கிறது...',
                'loading': 'ஏற்றுகிறது',
                'loading...': 'ஏற்றுகிறது...'
            };

            Array.from(this.$el.querySelectorAll('button, [role="button"], [aria-live], .amplify-button'))
                .forEach(element => {
                    const label = this.normalizedText(element.textContent);
                    const translation = runtimeTranslations[label];
                    if (translation && element.textContent.trim() !== translation) {
                        element.textContent = translation;
                    }
                });
        },
        closeModal() {
            this.trackModalClosed();
            this.$emit('close');
        },
        trackModalClosed() {
            if (this.closeTracked) return;
            this.closeTracked = true;
            analyticsTrack('auth_modal_closed', {
                flow: this.inferAuthFlow()
            });
        },
        switchToSignUp() {
            const tabs = this.$el.querySelectorAll('.amplify-tabs-item');
            if (tabs.length >= 2) {
                tabs[1].click();
                analyticsTrack('auth_flow_changed', {
                    flow: 'signup',
                    source: 'side_cta'
                });
            }
        },
        switchToSignIn() {
            const tabs = this.$el.querySelectorAll('.amplify-tabs-item');
            if (tabs.length >= 1) {
                tabs[0].click();
                analyticsTrack('auth_flow_changed', {
                    flow: 'login',
                    source: 'side_cta'
                });
            }
        },
        syncTabState() {
            const tabs = this.$el.querySelectorAll('.amplify-tabs-item');
            if (tabs.length >= 2) {
                this.isSignUp = tabs[1].getAttribute('data-state') === 'active';
            }
            const flow = this.inferAuthFlow();
            if (flow && flow !== this.lastAuthFlow) {
                this.lastAuthFlow = flow;
                analyticsTrack('auth_flow_changed', {
                    flow,
                    source: 'authenticator'
                });
            }
            const step = this.inferAuthStep();
            if (step && step !== this.lastAuthStep) {
                this.lastAuthStep = step;
                analyticsTrack('auth_step_viewed', {
                    flow,
                    step,
                    source: 'authenticator',
                    path: this.$route?.fullPath || window.location.pathname
                });
            }
        },
        setupTabObserver() {
            const authenticator = this.$el.querySelector('[data-amplify-authenticator]');
            if (!authenticator) {
                setTimeout(() => this.setupTabObserver(), 200);
                return;
            }
            this.observer = new MutationObserver(() => {
                this.syncTabState();
                this.trackVisibleAuthError();
                this.scheduleAuthEnhancements();
            });
            this.observer.observe(authenticator, {
                attributes: true,
                subtree: true,
                childList: true,
                attributeFilter: ['data-state', 'class', 'aria-invalid']
            });
            this.syncTabState();
            this.attachAuthAttemptTracking(authenticator);
            this.trackVisibleAuthError();
            this.scheduleAuthEnhancements();
        },
        scheduleAuthEnhancements() {
            if (this.authEnhancementPending) return;
            this.authEnhancementPending = true;

            requestAnimationFrame(() => {
                this.authEnhancementPending = false;
                if (!this.$el) return;
                this.localizeAuthRuntimeText();
                this.enhanceUsernameCaseHint();
                this.enhanceForgotPasswordInstruction();
            });
        },
        attachAuthAttemptTracking(authenticator) {
            if (this.formListenerAttached) return;
            this.formListenerAttached = true;
            authenticator.addEventListener('submit', () => {
                this.captureForgotPasswordUsername();
                this.trackAuthAttempt('submit');
            }, true);
            authenticator.addEventListener('click', (event) => {
                const button = event.target.closest('button');
                if (!button) return;
                const label = this.normalizedText(button.textContent);
                if (!label) return;
                if (this.isPrimaryAuthAction(label)) {
                    this.captureForgotPasswordUsername();
                    this.trackAuthAttempt('button');
                }
            }, true);
            authenticator.addEventListener('input', () => {
                this.captureForgotPasswordUsername();
            }, true);
        },
        trackAuthAttempt(source) {
            analyticsTrack('auth_attempt', {
                flow: this.inferAuthFlow(),
                step: this.inferAuthStep(),
                source,
                ...this.getAuthFormAnalytics(),
                path: this.$route?.fullPath || window.location.pathname
            });
        },
        trackVisibleAuthError() {
            const selectors = [
                '[role="alert"]',
                '.amplify-alert',
                '.amplify-field__error-message',
                '[data-variation="error"]'
            ];
            const errorText = selectors
                .flatMap(selector => Array.from(this.$el.querySelectorAll(selector)))
                .map(el => this.normalizedText(el.textContent))
                .find(Boolean);

            if (!errorText) return;
            this.trackAuthFailure(errorText, 'authenticator_ui');
        },
        trackAuthFailure(error, source) {
            const errorCode = this.sanitizeAuthError(error);
            const now = Date.now();
            if (errorCode === this.lastErrorCode && now - this.lastErrorTrackedAt < 2000) return;
            this.lastErrorCode = errorCode;
            this.lastErrorTrackedAt = now;
            analyticsTrack('auth_failure', {
                flow: this.inferAuthFlow(),
                step: this.inferAuthStep(),
                error_code: errorCode,
                source,
                ...this.getAuthFormAnalytics(),
                path: this.$route?.fullPath || window.location.pathname
            });
        },
        inferAuthFlow() {
            const text = this.normalizedText(this.$el?.textContent);
            if (text.includes('forgot password') || text.includes('reset password') || text.includes('கடவுச்சொல்லை மீட்டமை') || text.includes('குறியீட்டை அனுப்புக')) return 'forgot_password';
            if (text.includes('verification code') || text.includes('confirmation code') || text.includes('confirm') || text.includes('உறுதிப்படுத்தும் குறியீடு') || text.includes('உறுதிப்படுத்து')) return 'confirm_signup';
            return this.isSignUp ? 'signup' : 'login';
        },
        inferAuthStep() {
            const text = this.normalizedText(this.$el?.textContent);
            if (text.includes('verification code') || text.includes('confirmation code') || text.includes('உறுதிப்படுத்தும் குறியீடு')) return 'confirm_code';
            if (text.includes('forgot password') || text.includes('reset password') || text.includes('கடவுச்சொல்லை மீட்டமை') || text.includes('குறியீட்டை அனுப்புக')) return 'request_reset';
            return this.isSignUp ? 'signup_form' : 'login_form';
        },
        isPrimaryAuthAction(label) {
            return [
                'sign in',
                'sign up',
                'create account',
                'forgot password',
                'reset password',
                'send code',
                'submit',
                'confirm',
                'change password',
                'உள்நுழைக',
                'பதிவு செய்க',
                'கணக்கை உருவாக்குக',
                'குறியீட்டை அனுப்புக',
                'சமர்ப்பி',
                'உறுதிப்படுத்து',
                'கடவுச்சொல்லை மாற்று'
            ].some(action => label.includes(action));
        },
        sanitizeAuthError(error) {
            const message = this.normalizedText(
                typeof error === 'string'
                    ? error
                    : error?.message || error?.name || error?.code || ''
            );
            if (!message) return 'unknown';
            if (message.includes('incorrect username') || message.includes('incorrect password') || message.includes('not authorized')) return 'invalid_credentials';
            if (message.includes('user does not exist') || message.includes('usernotfound')) return 'user_not_found';
            if (message.includes('already exists') || message.includes('usernameexists')) return 'account_already_exists';
            if (message.includes('not confirmed')) return 'user_not_confirmed';
            if (message.includes('invalid verification') || message.includes('code mismatch')) return 'invalid_code';
            if (message.includes('expired')) return 'expired_code';
            if (message.includes('password')) return 'password_policy';
            if (message.includes('limit') || message.includes('attempt')) return 'attempt_limit';
            if (message.includes('network')) return 'network_error';
            return 'auth_error';
        },
        normalizedText(value) {
            return String(value || '').replace(/\s+/g, ' ').trim().toLowerCase();
        },
        captureForgotPasswordUsername() {
            const usernameInput = this.findUsernameInput();
            const username = usernameInput?.value?.trim();
            if (username) {
                this.lastForgotPasswordUsername = username;
            }
        },
        findUsernameInput() {
            const candidates = Array.from(this.$el.querySelectorAll('input'));
            return candidates.find(input => {
                const name = this.normalizedText(input.getAttribute('name'));
                const autocomplete = this.normalizedText(input.getAttribute('autocomplete'));
                const label = this.normalizedText(input.getAttribute('aria-label'));
                const placeholder = this.normalizedText(input.getAttribute('placeholder'));
                const id = this.normalizedText(input.getAttribute('id'));

                return [name, autocomplete, label, placeholder, id].some(value =>
                    value.includes('username') || value.includes('email')
                );
            });
        },
        findPasswordInput() {
            return Array.from(this.$el.querySelectorAll('input'))
                .find(input => input.getAttribute('type') === 'password');
        },
        getAuthFormAnalytics() {
            const username = this.findUsernameInput()?.value?.trim() || '';
            const password = this.findPasswordInput()?.value || '';

            return {
                username: username || undefined,
                has_username: username.length > 0,
                has_password: password.length > 0
            };
        },
        isForgotPasswordConfirmStep() {
            const text = this.normalizedText(this.$el?.textContent);
            return (
                (text.includes('code') || text.includes('confirmation') || text.includes('குறியீடு')) &&
                (text.includes('new password') || text.includes('change password') || text.includes('reset password') || text.includes('புதிய கடவுச்சொல்') || text.includes('கடவுச்சொல்லை மாற்று') || text.includes('கடவுச்சொல்லை மீட்டமை'))
            );
        },
        enhanceUsernameCaseHint() {
            const usernameInput = this.findUsernameInput();
            const existing = this.$el.querySelector('.username-case-hint');
            if (!usernameInput || this.isForgotPasswordConfirmStep()) {
                existing?.remove();
                return;
            }

            const message = this.authText('usernameCaseHint');
            if (existing) {
                existing.textContent = message;
                return;
            }

            const hint = document.createElement('p');
            hint.className = 'username-case-hint';
            hint.textContent = message;

            const field = usernameInput.closest('.amplify-field') || usernameInput.parentElement;
            if (field) {
                field.insertAdjacentElement('afterend', hint);
            }
        },
        enhanceForgotPasswordInstruction() {
            const authenticator = this.$el.querySelector('[data-amplify-authenticator]');
            if (!authenticator) return;

            const existing = authenticator.querySelector('.forgot-reset-instruction');
            if (!this.isForgotPasswordConfirmStep()) {
                existing?.remove();
                return;
            }

            const username = this.lastForgotPasswordUsername;
            const message = username
                ? this.authText('forgotInstructionWithUsername', { username })
                : this.authText('forgotInstruction');

            if (existing) {
                existing.textContent = message;
                return;
            }

            const instruction = document.createElement('p');
            instruction.className = 'forgot-reset-instruction';
            instruction.textContent = message;

            const form = authenticator.querySelector('form');
            const heading = form?.querySelector('h1, h2, h3, [data-amplify-heading]');
            if (heading?.parentNode) {
                heading.insertAdjacentElement('afterend', instruction);
            } else if (form) {
                form.prepend(instruction);
            }
        }
    }
}
</script>

<style scoped>
.login-overlay {
    position: fixed;
    inset: 0;
    z-index: 10000;
    background: rgba(0, 0, 0, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
}

.login-modal {
    position: relative;
    max-width: 900px;
    width: 100%;
    max-height: 90vh;
    overflow-y: auto;
}

.login-close-button {
    position: absolute;
    top: 12px;
    right: 12px;
    z-index: 10;
    background: rgba(0, 0, 0, 0.3);
    border: none;
    color: #fff;
    cursor: pointer;
    border-radius: 50%;
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background 0.2s ease;
}

.login-close-button:hover {
    background: rgba(0, 0, 0, 0.5);
}

.login-container {
    display: flex;
    width: 100%;
    min-height: 550px;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
}

.login-left {
    flex: 1 1 50%;
    min-width: 0;
    background: #1a1a1a;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 30px;
}

.auth-wrapper {
    width: 100%;
    max-width: 320px;
}

.login-right {
    flex: 1 1 50%;
    min-width: 0;
    background: linear-gradient(135deg, #8b9cf7 0%, #a78bfa 50%, #6b7de0 100%);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px;
}

.login-right-content {
    text-align: center;
    color: #ffffff;
}

.login-right-content h2 {
    font-size: 32px;
    font-weight: 700;
    margin: 0 0 12px 0;
}

.login-right-content p {
    font-size: 16px;
    margin: 0 0 28px 0;
    opacity: 0.9;
    line-height: 1.5;
}

.cta-button {
    background: transparent;
    color: #ffffff;
    border: 2px solid #ffffff;
    border-radius: 25px;
    padding: 12px 48px;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: inherit;
    letter-spacing: 0.5px;
}

.cta-button:hover {
    background: #ffffff;
    color: #6b7de0;
}

.login-success {
    text-align: center;
    color: #ccc;
    font-size: 16px;
}

:deep(.forgot-reset-instruction) {
    margin: 0 0 18px;
    padding: 12px 14px;
    border-radius: 8px;
    background: rgba(255, 68, 68, 0.08);
    border: 1px solid rgba(255, 68, 68, 0.18);
    color: #4b5563;
    font-size: 14px;
    line-height: 1.5;
}

:deep(.username-case-hint) {
    margin: -8px 0 14px;
    color: #6b7280;
    font-size: 13px;
    line-height: 1.4;
}

/* Responsive: stack vertically on mobile */
@media (max-width: 768px) {
    .login-overlay {
        padding: 0;
    }

    .login-modal {
        max-width: 100%;
        max-height: 100vh;
        border-radius: 0;
    }

    .login-container {
        flex-direction: column;
        min-height: auto;
        border-radius: 0;
        box-shadow: none;
    }

    .login-left {
        padding: 30px 20px;
    }

    .login-right {
        padding: 30px 20px;
    }

    .login-right-content h2 {
        font-size: 24px;
    }

    .login-right-content p {
        margin-bottom: 20px;
    }
}
</style>
